'use client';

import React from 'react';

interface TabItem {
  key: string;
  label: string;
  icon?: string;
  count?: number | string;
}

interface TabNavProps {
  tabs: TabItem[];
  activeTab: string;
  onChange: (key: string) => void;
  variant?: 'pills' | 'underline';
}

const TabNav = React.memo(function TabNav({
  tabs,
  activeTab,
  onChange,
  variant = 'pills',
}: TabNavProps) {
  if (variant === 'underline') {
    return (
      <div className="flex border-b border-slate-200 dark:border-slate-700">
        {tabs.map((tab) => {
          const isActive = tab.key === activeTab;
          return (
            <button
              key={tab.key}
              onClick={() => onChange(tab.key)}
              className={`
                relative flex items-center gap-2 px-4 py-3 text-sm font-medium whitespace-nowrap transition-colors duration-200
                ${
                  isActive
                    ? 'text-[var(--hrm-primary-dark)] dark:text-[var(--hrm-primary)]'
                    : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200'
                }
              `}
            >
              {tab.icon && (
                <i className={`${tab.icon} text-lg`} />
              )}
              <span>{tab.label}</span>
              {tab.count !== undefined && tab.count !== null && (
                <span
                  className={`
                    inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[11px] font-semibold leading-none transition-colors duration-200
                    ${
                      isActive
                        ? 'bg-[#FFD6D6] text-[var(--hrm-primary-dark)] dark:bg-[var(--hrm-primary-dark)]/40 dark:text-[#FF8080]'
                        : 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400'
                    }
                  `}
                >
                  {tab.count}
                </span>
              )}
              {/* Animated underline indicator */}
              <span
                className={`
                  absolute bottom-0 left-0 right-0 h-0.5 rounded-full transition-transform duration-200 origin-left
                  ${isActive ? 'bg-[var(--hrm-primary-dark)] dark:bg-[var(--hrm-primary)] scale-x-100' : 'scale-x-0'}
                `}
              />
            </button>
          );
        })}
      </div>
    );
  }

  // Pills variant (default)
  return (
    <div className="flex flex-wrap gap-1 p-1 bg-slate-100 dark:bg-slate-800/60 rounded-xl">
      {tabs.map((tab) => {
        const isActive = tab.key === activeTab;
        return (
          <button
            key={tab.key}
            onClick={() => onChange(tab.key)}
            className={`
              relative flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg whitespace-nowrap transition-all duration-200
              ${
                isActive
                  ? 'bg-[var(--hrm-primary-dark)] text-white shadow-sm shadow-[var(--hrm-primary-dark)]/25'
                  : 'text-slate-600 hover:bg-slate-200/80 hover:text-slate-900 dark:text-slate-300 dark:hover:bg-slate-700/60 dark:hover:text-white'
              }
            `}
          >
            {tab.icon && (
              <i className={`${tab.icon} text-lg`} />
            )}
            <span>{tab.label}</span>
            {tab.count !== undefined && tab.count !== null && (
              <span
                className={`
                  inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[11px] font-semibold leading-none transition-colors duration-200
                  ${
                    isActive
                      ? 'bg-white/20 text-white'
                      : 'bg-slate-200 text-slate-500 dark:bg-slate-700 dark:text-slate-400'
                  }
                `}
              >
                {tab.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
});

export default TabNav;
